import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import i18n from "i18next";
import { LuALargeSmall } from "react-icons/lu";
import FontPickerGrid from "../editor_shortcuts/FontPickerGrid";
import { SUPPORTED_GOOGLE_FONTS } from "../../../../common/font_extractor.es6";

// Font button in the floating canvas toolbar: retypesets every BasicText /
// RichText box at once, without opening Global Config. The scope radios pick
// how far the change reaches and default to "this scene" — a stray click here
// must never rewrite the whole training.
//
// The panel is PORTALED to <body> and pinned to the FAR LEFT of the window,
// just below the toolbar — over the gray gutter beside the canvas — so it
// barely covers the scene whose text it live-previews. (Plain popover, not a
// dialog on a dialog, so the "no popup over a popup" rule is satisfied.)
export default function CanvasFontButton({ value, onSelect, onPreview }) {
  const [open, setOpen] = useState(false);
  const [allScenes, setAllScenes] = useState(false);
  const [pos, setPos] = useState(null);
  const rootRef = useRef(null);
  const popRef = useRef(null);

  const close = () => {
    if (onPreview) onPreview(null);
    setOpen(false);
  };

  const place = () => {
    const btn = rootRef.current;
    if (!btn) return;
    const bar = btn.closest(".canvasOverlay") || btn;
    const r = bar.getBoundingClientRect();
    // Align with the toolbar's LEFT edge: the toolbar already starts right
    // after the left panel (EditorApp shifts it with carouselShow), so the
    // panel opens in the gutter without covering the scenes list — and still
    // hugs the window edge when the left panel is closed.
    setPos({ left: Math.max(8, r.left), top: r.bottom + 10 });
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      const inBtn = rootRef.current && rootRef.current.contains(e.target);
      const inPop = popRef.current && popRef.current.contains(e.target);
      if (!inBtn && !inPop) close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDocDown, true);
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      document.removeEventListener("mousedown", onDocDown, true);
      document.removeEventListener("keydown", onKey, true);
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
    // Only re-subscribes on `open` — `close`/`place` are fresh functions each
    // render but always close over this render's props, same as FontFamilyButton.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <span className="canvasFontBtn" ref={rootRef}>
      <button
        type="button"
        className={"canvasOverlayBtn" + (open ? " is-active" : "")}
        title={i18n.t("global_config.course_font")}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => {
          if (open) {
            close();
          } else {
            place();
            setOpen(true);
          }
        }}
      >
        <LuALargeSmall size={20} />
      </button>
      {open &&
        pos &&
        ReactDOM.createPortal(
          <div
            ref={popRef}
            className="canvasFontPopover"
            style={{ position: "fixed", left: pos.left, top: pos.top }}
            role="menu"
          >
            <div className="canvasFontPopover__head">
              <span className="canvasFontPopover__title">
                {i18n.t("global_config.course_font")}
              </span>
              <div
                className="canvasFontPopover__scope"
                role="radiogroup"
                aria-label={i18n.t("global_config.course_font")}
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={!allScenes}
                  onClick={() => setAllScenes(false)}
                >
                  {i18n.t("global_config.course_font_scope_scene")}
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={allScenes}
                  onClick={() => setAllScenes(true)}
                >
                  {i18n.t("global_config.course_font_scope_course")}
                </button>
              </div>
            </div>
            {/* The only scroller — FontPickerGrid's own max-height is dropped
              here (see _canvasOverlay.scss) so there aren't two nested bars. */}
            <div className="canvasFontPopover__list">
              <FontPickerGrid
                value={value}
                googleFonts={SUPPORTED_GOOGLE_FONTS}
                onPreview={onPreview}
                onSelect={(next) => {
                  onSelect(next, allScenes);
                  close();
                }}
              />
            </div>
          </div>,
          document.body
        )}
    </span>
  );
}

CanvasFontButton.propTypes = {
  /** Current font of the scene's text (drives the picker's active row). */
  value: PropTypes.string,
  /** (fontFamily, allScenes) — commits the pick. */
  onSelect: PropTypes.func.isRequired,
  /** (fontFamily|null) — live preview on the current scene's text boxes. */
  onPreview: PropTypes.func,
};
